import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SprayRecord, AppliedChemical, DroneFlightEntry, WeatherConditions } from '../models/spray-record.model';
import { FieldService } from './field.service';
import { CustomerService } from './customer.service';
import { OrganizationContextService } from './organization-context.service';

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
}

/**
 * DTOs for creating and updating spray records
 */
export interface CreateSprayRecordDto {
  fieldId: string;
  applicationDate: string;
  status?: 'pending' | 'completed' | 'cancelled';
  // State Applicator certification snapshot
  certificationId?: string;
  certificationName?: string;
  certificationNumber?: string;
  certificationState?: string;
  certificationExpiration?: string;
  // Organization FAA certifications snapshot (for compliance reporting)
  orgFaa44807Number?: string;
  orgFaa44807Expiration?: string;
  orgFaaPart137Number?: string;
  orgFaaPart137Expiration?: string;
  chemicals?: any[];
  droneFlights?: any[];
  // Spray details
  whatWasTreated?: string;
  targetPest?: string;
  nozzleDescription?: string;
  nozzleToTargetDistance?: number;
  pumpPressure?: number;
  travelSpeed?: number;
  driftManagementPractices?: string;
  applicationTypes?: string[];
  applicationTypeOther?: string;
  // Tank mix
  totalVolume?: number;
  tankMixNotes?: string;
  // Weather
  weatherTemperature?: number;
  weatherHumidity?: number;
  weatherWindSpeed?: number;
  weatherWindDirection?: string;
  weatherConditions?: string;
  // Incident information
  takeoffLandingDamage?: boolean;
  takeoffLandingDamageDescription?: string;
  equipmentMalfunction?: boolean;
  equipmentMalfunctionType?: string;
  equipmentMalfunctionDescription?: string;
  hasAccident?: boolean;
  accidentType?: string;
  accidentDescription?: string;
  uaLossType?: string;
  // Safety & compliance
  reentryInterval?: string;
  ppeRequired?: string[];
  rinseEquipmentPostApplication?: boolean;
  // Emergency contacts
  emergencyContactNumber?: string;
  stateEmergencyContactNumber?: string;
  // Load/mix location (maps to use_field_for_load_mix, load_mix_latitude/longitude)
  useFieldForLoadMix?: boolean;
  loadMixLatitude?: number;
  loadMixLongitude?: number;
  // Visual Observer & NOTAM
  visualObserver?: string;
  voTested?: boolean;
  notam?: string;
  // Checklist
  preFlightChecklist?: boolean;
  bufferZonesVerified?: boolean;
  notes?: string;
}

export interface UpdateSprayRecordDto extends Partial<CreateSprayRecordDto> {
  changeDescription?: string;
}

/**
 * Simple spray record for list views
 * Flattened structure for easy display
 */
export interface SprayRecordListItem {
  id: string;
  date: string;
  fieldName: string;
  customerName?: string;
  customerId?: string;
  cropType: string;
  acreage: number;
  /**
   * Sum of flight counts this record contributes to a filing (FAA §F.2.d),
   * each aircraft floored to 1 as the report floors it. **Null when the
   * record has no aircraft** — it renders "—" rather than a number, so a
   * record that did not fly never advertises a flight the filing lacks.
   */
  filedFlights: number | null;
  /** At least one aircraft has no count and will be filed as 1. */
  needsFlightCount: boolean;
  chemicalName: string;
  applicationRate: string;
  pilotName: string;
  droneId: string;
  status: 'completed' | 'pending' | 'cancelled';
  weatherConditions?: string;
  notes?: string;
}

/**
 * Whether a drone is free for a requested slot. `message` names the clashing
 * booking when it is not.
 */
export interface DroneAvailability {
  available: boolean;
  message?: string;
}

/**
 * Spray Record Service
 *
 * Handles spray record operations with real API integration.
 * Uses signal-based state management for reactive UI updates.
 *
 * Reference: /agent-os/specs/2026-01-07-spray-records-backend/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class SprayRecordService {
  private http = inject(HttpClient);
  private organizationContext = inject(OrganizationContextService);
  private apiUrl = environment.apiUrl;
  private fieldService = inject(FieldService);
  private customerService = inject(CustomerService);

  // Signal-based state management
  private recordsSignal = signal<any[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Public computed signals for components
  readonly records = computed(() => this.recordsSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  // Computed helpers for dashboard stats
  readonly completedRecords = computed(() =>
    this.recordsSignal().filter(r => r.status === 'completed')
  );

  readonly pendingRecords = computed(() =>
    this.recordsSignal().filter(r => r.status === 'pending')
  );

  readonly totalAcreageCompleted = computed(() =>
    this.completedRecords().reduce((sum, r) => {
      return sum + (r.totalAcreage || 0);
    }, 0)
  );

  constructor() {
    // React to organization context changes — load records when org becomes available
    effect(() => {
      const orgId = this.organizationContext.currentOrganizationId();
      if (orgId) {
        this.loadRecords();
      }
    });
  }

  /**
   * Load spray records from API
   */
  async loadRecords(options: {
    status?: string;
    fieldId?: string;
    pilotId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<void> {
    if (!this.organizationContext.getCurrentOrganizationId()) {
      return;
    }

    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const params: any = {
        page: options.page || 1,
        limit: options.limit || 100
      };
      if (options.status) params.status = options.status;
      if (options.fieldId) params.fieldId = options.fieldId;
      if (options.pilotId) params.pilotId = options.pilotId;
      if (options.startDate) params.startDate = options.startDate;
      if (options.endDate) params.endDate = options.endDate;

      const response = await firstValueFrom(
        this.http.get<ApiResponse<any[]>>(`${this.apiUrl}/spray-records`, { params })
      );

      if (response.success && response.data) {
        this.recordsSignal.set(response.data);
      } else {
        throw new Error(response.error || 'Failed to load spray records');
      }
    } catch (error: any) {
      console.error('Error loading spray records:', error);
      this.errorSignal.set(error.message || 'Failed to load spray records');
      // Keep existing data on error
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Get all records (from signal)
   */
  getRecords(): any[] {
    return this.recordsSignal();
  }

  /**
   * Get records as flattened list items for display
   */
  getRecordsAsListItems(): SprayRecordListItem[] {
    return this.recordsSignal().map(r => this.toListItem(r));
  }

  /**
   * Convert full SprayRecord to simplified list item
   */
  private toListItem(record: any): SprayRecordListItem {
    // Backend returns simplified list items already
    return {
      id: record.id,
      date: record.applicationDate || record.date,
      fieldName: record.fieldName || 'Unknown Field',
      customerName: record.customerName,
      customerId: record.customerId,
      cropType: record.cropType || 'Unknown',
      acreage: record.totalAcreage || 0,
      // `?? null`, not `|| null` — a genuine 0 would be lost by the latter.
      filedFlights: record.filedFlights ?? null,
      needsFlightCount: record.needsFlightCount === true,
      chemicalName: record.chemicals?.[0]?.name || 'N/A',
      applicationRate: record.chemicals?.[0]?.rate || 'N/A',
      pilotName: record.pilotName || 'Unknown',
      droneId: record.droneNames?.[0] || 'N/A',
      status: record.status || 'pending',
      weatherConditions: record.weatherConditions,
      notes: record.notes
    };
  }

  /**
   * Get a single record by ID from API
   */
  async getRecordById(id: string): Promise<any | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<any>>(`${this.apiUrl}/spray-records/${id}`)
      );

      if (response.success && response.data) {
        return response.data;
      }

      return null;
    } catch (error: any) {
      console.error('Error fetching spray record:', error);
      this.errorSignal.set(error.message || 'Failed to fetch spray record');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Is a drone free for a slot, or is it already flying for someone else?
   *
   * Advisory only — a failed check resolves to "available" so a flaky network
   * can never stop a user saving.
   */
  async checkDroneAvailability(params: {
    droneId: string;
    droneSource: string;
    applicationDate: string;
    startTime: string;
    endTime: string;
    excludeSprayRecordId?: string;
  }): Promise<DroneAvailability> {
    try {
      const { excludeSprayRecordId, ...rest } = params;
      const query = excludeSprayRecordId ? { ...rest, excludeSprayRecordId } : rest;

      const response = await firstValueFrom(
        this.http.get<ApiResponse<DroneAvailability>>(
          `${this.apiUrl}/spray-records/drone-flights/availability`,
          { params: query }
        )
      );

      return response.data ?? { available: true };
    } catch (error: any) {
      console.error('Error checking drone availability:', error);
      return { available: true };
    }
  }

  /**
   * Create a new spray record
   */
  async createRecord(dto: CreateSprayRecordDto): Promise<any | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<any>>(`${this.apiUrl}/spray-records`, dto)
      );

      if (response.success && response.data) {
        // Reload the full list to get properly shaped list items
        // The create response has a different shape (full record) than list items
        await this.loadRecords();
        return response.data;
      }

      throw new Error(response.error || 'Failed to create spray record');
    } catch (error: any) {
      console.error('Error creating spray record:', error);
      this.errorSignal.set(error.message || 'Failed to create spray record');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Update an existing spray record
   */
  async updateRecord(id: string, dto: UpdateSprayRecordDto): Promise<any | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.put<ApiResponse<any>>(`${this.apiUrl}/spray-records/${id}`, dto)
      );

      if (response.success && response.data) {
        // Reload the full list to get properly shaped list items
        // The update response has a different shape (full record) than list items
        await this.loadRecords();

        return response.data;
      }

      throw new Error(response.error || 'Failed to update spray record');
    } catch (error: any) {
      console.error('Error updating spray record:', error);
      this.errorSignal.set(error.message || 'Failed to update spray record');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Delete a spray record
   */
  async deleteRecord(id: string): Promise<boolean> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.delete<ApiResponse<void>>(`${this.apiUrl}/spray-records/${id}`)
      );

      if (response.success) {
        // Remove from local state
        this.recordsSignal.update(records => records.filter(r => r.id !== id));
        return true;
      }

      throw new Error(response.error || 'Failed to delete spray record');
    } catch (error: any) {
      console.error('Error deleting spray record:', error);
      this.errorSignal.set(error.message || 'Failed to delete spray record');
      return false;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Get unique field names for filter dropdowns
   */
  getUniqueFields(): string[] {
    const fields = this.recordsSignal()
      .map(r => r.fieldName)
      .filter((name): name is string => !!name);
    return [...new Set(fields)];
  }

  /**
   * Get unique pilot names for filter dropdowns
   */
  getUniquePilots(): string[] {
    const pilots = this.recordsSignal()
      .map(r => r.pilotName)
      .filter((name): name is string => !!name);
    return [...new Set(pilots)];
  }

  /**
   * Filter records by various criteria (client-side filtering)
   */
  filterRecords(filters: {
    status?: string;
    field?: string;
    pilot?: string;
    dateFrom?: string;
    dateTo?: string;
  }): any[] {
    let results = [...this.recordsSignal()];

    if (filters.status) {
      results = results.filter(r => r.status === filters.status);
    }

    if (filters.field) {
      results = results.filter(r =>
        r.fieldName?.toLowerCase().includes(filters.field!.toLowerCase())
      );
    }

    if (filters.pilot) {
      results = results.filter(r =>
        r.pilotName?.toLowerCase().includes(filters.pilot!.toLowerCase())
      );
    }

    if (filters.dateFrom) {
      results = results.filter(r => r.applicationDate >= filters.dateFrom!);
    }

    if (filters.dateTo) {
      results = results.filter(r => r.applicationDate <= filters.dateTo!);
    }

    return results;
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorSignal.set(null);
  }
}
